import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { IMG } from "../data/content";
import { MaskedLine, Img } from "./Reveal";
import { scrollToId } from "./Navbar";
import { useEnquiry } from "./EnquiryContext";
import { BookingBar } from "./BookingBar";

export const Hero = () => {
  const ref = useRef(null);
  const { openEnquiry } = useEnquiry();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative">
      <div className="relative h-[100svh] min-h-[620px] overflow-hidden">
        <motion.div style={{ y: bgY }} className="absolute inset-0">
          <motion.div
            initial={{ scale: 1.18 }}
            animate={{ scale: 1 }}
            transition={{ duration: 2.6, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-0"
          >
            <Img
              src={IMG.heroTiger}
              alt="Tiger in the forests of Madhya Pradesh"
              className="w-full h-full object-cover"
            />
          </motion.div>
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-forest-950 via-forest-950/35 to-forest-950/55" />
        <div className="absolute inset-0 bg-gradient-to-r from-forest-950/70 via-transparent to-transparent" />

        <motion.div
          style={{ opacity: contentOpacity }}
          className="relative z-10 h-full max-w-7xl mx-auto px-5 sm:px-8 flex flex-col justify-end pb-32 lg:pb-40"
        >
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="flex items-center gap-4 mb-7"
          >
            <span className="h-px w-14 bg-gold" />
            <span className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.3em] text-gold">
              HiddenBiomes · Madhya Pradesh
            </span>
          </motion.div>

          <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight text-cream leading-[1.05] max-w-3xl">
            <MaskedLine delay={0.35}>Discover the Wild.</MaskedLine>
            <MaskedLine delay={0.55}>
              <span className="italic text-gold">Explore the Unseen.</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9 }}
            className="mt-6 max-w-xl text-base text-cream/75 leading-relaxed"
          >
            Curated wildlife, adventure and nature experiences across Madhya
            Pradesh and beyond — thoughtfully planned, beautifully guided.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-explore-tours-btn"
              onClick={() => scrollToId("tours")}
              className="group bg-gold text-forest-950 font-mono text-xs uppercase tracking-[0.22em] px-8 py-4 flex items-center gap-3 hover:bg-cream transition-colors duration-300"
            >
              Explore Tours
              <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1.5" />
            </button>
            <button
              data-testid="hero-plan-journey-btn"
              onClick={() => openEnquiry()}
              className="border border-cream/30 text-cream font-mono text-xs uppercase tracking-[0.22em] px-8 py-4 hover:border-gold hover:text-gold transition-colors duration-300"
            >
              Plan Your Journey
            </button>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="absolute bottom-32 lg:bottom-40 right-6 sm:right-10 z-10 hidden sm:flex flex-col items-center gap-3"
        >
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-cream/50 [writing-mode:vertical-lr]">
            Scroll
          </span>
          <motion.span
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
          >
            <ArrowDown size={14} className="text-gold" />
          </motion.span>
        </motion.div>
      </div>

      <BookingBar />
    </section>
  );
};
